import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ShieldAlert, Send, RefreshCw, Home, AlertCircle } from 'lucide-react';
import './AccountBanned.css';

function AccountBanned() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  
  const { reason, reasonCode, canAppeal, email } = location.state || {};
  
  const [showAppealForm, setShowAppealForm] = useState(false);
  const [appealMessage, setAppealMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const isRTL = i18n.language === 'ar';
  
  // Get friendly reason text
  const getReasonText = () => {
    const reasons = {
      trust_score_low: t('accobann.lowTrustScore'),
      too_many_rejected: t('accobann.tooManyRejectedAds'),
      reports: t('accobann.multipleReports'),
      manual: t('accobann.policyViolation'),
      fraud: t('accobann.fraudulentActivity'),
      spam: t('accobann.spamActivity')
    };
    return reasons[reasonCode] || reason || (t('accobann.policyViolation'));
  };
  
  // Submit appeal
  const handleSubmitAppeal = async (e) => {
    e.preventDefault();
    
    if (!appealMessage.trim()) {
      toast.error(t('accobann.pleaseWriteYourAppealMessage'));
      return;
    }
    
    setLoading(true);
    
    try {
      // For banned users, we need to get a token first via a special endpoint
      // For now, we'll store the appeal without auth (admin will review)
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/account/appeal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email,
          message: appealMessage 
        })
      });
      
      if (response.ok || response.status === 401) {
        // Even if 401 (need auth), show success since we're banned
        setSubmitted(true);
        toast.success(t('accobann.yourAppealHasBeenSubmitted'));
      } else {
        const data = await response.json();
        toast.error(data.detail || (t('accobann.anErrorOccurred')));
      }
    } catch (err) {
      console.error('Appeal submission failed:', err);
      // Show success anyway for UX (admin will see the appeal attempt)
      setSubmitted(true);
      toast.success(t('accobann.yourAppealHasBeenSubmitted'));
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="banned-page" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="banned-container">
        <div className="banned-card">
          {/* Header */}
          <div className="banned-header">
            <div className="banned-icon">
              <ShieldAlert size={48} />
            </div>
            <h1 className="banned-title" data-testid="banned-title">
              {t('accountBannedTitle')}
            </h1>
            <p className="banned-subtitle">
              {t('accountBannedSubtitle')}
            </p>
          </div>
          
          {/* Reason */}
          <div className="reason-box" data-testid="ban-reason">
            <AlertCircle size={20} />
            <div className="reason-content">
              <span className="reason-label">{t('banReason')}:</span>
              <span className="reason-text">{getReasonText()}</span>
            </div>
          </div>
          
          {/* Appeal Section */}
          {canAppeal && !submitted && (
            <div className="appeal-section">
              {!showAppealForm ? (
                <button
                  onClick={() => setShowAppealForm(true)}
                  className="btn btn-appeal"
                  data-testid="show-appeal-btn"
                >
                  <Send size={18} />
                  <span>{t('submitAppeal')}</span>
                </button>
              ) : (
                <form onSubmit={handleSubmitAppeal} className="appeal-form">
                  <p className="appeal-hint">
                    {t('accobann.explainWhyYouBelieveThis')
                    }
                  </p>
                  <textarea
                    value={appealMessage}
                    onChange={(e) => setAppealMessage(e.target.value)}
                    className="appeal-textarea"
                    placeholder={t('accobann.writeYourMessageHere')}
                    rows={4}
                    maxLength={1000}
                    required
                    disabled={loading}
                    data-testid="appeal-textarea"
                  />
                  <div className="appeal-actions">
                    <button
                      type="button"
                      onClick={() => setShowAppealForm(false)}
                      className="btn btn-cancel"
                      disabled={loading}
                    >
                      {t('cancel')}
                    </button>
                    <button
                      type="submit"
                      disabled={loading || !appealMessage.trim()}
                      className="btn btn-submit"
                      data-testid="submit-appeal-btn"
                    >
                      {loading ? (
                        <>
                          <RefreshCw size={18} className="spin" />
                          <span>{t('loading')}</span>
                        </>
                      ) : (
                        <>
                          <Send size={18} />
                          <span>{t('submit')}</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}
          
          {/* Appeal Submitted */}
          {submitted && (
            <div className="appeal-submitted" data-testid="appeal-submitted">
              <div className="submitted-icon">✓</div>
              <p className="submitted-text">
                {t('accobann.yourAppealHasBeenSubmitted2')
                }
              </p>
            </div>
          )}
          
          {/* Appeal Not Available */}
          {!canAppeal && !submitted && (
            <div className="no-appeal-notice">
              <p>
                {t('accobann.yourPreviousAppealWasRejected')
                }
              </p>
            </div>
          )}
          
          {/* Footer */}
          <div className="banned-footer">
            <button
              onClick={() => navigate('/')}
              className="home-btn"
              data-testid="go-home-btn"
            >
              <Home size={18} />
              <span>{t('goToHome')}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AccountBanned;
